/**
 * Inventory scan box: capture (shared scanner wedge) → resolve
 * (`GET /variants?barcode=`, a 0-or-1 contract) → apply | miss.
 *
 * Floor screens are scanner-first, so the field is always live: a hardware
 * wedge scan is captured even when focus sits elsewhere, and a typed value
 * submits on Enter. A miss is non-blocking and always shows the raw scanned
 * value so the operator can see what the gun actually read.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { ScanBarcode } from "lucide-react";
import { Input, Spinner } from "@erp/ui";

// Shared scanner utility — imported RELATIVELY per the workspace contract.
import { useScannerInput } from "../../shared/scanner/src/react";

import { lookupVariantByBarcode, type VariantOption } from "../api";

function ScannerCapture({ onScan }: { onScan: (value: string) => void }) {
  useScannerInput({ onScan: (scan) => onScan(scan.value) });
  return null;
}

export interface ScanBoxProps {
  id: string;
  label: string;
  /** Exactly one variant matched. */
  onResolved: (variant: VariantOption) => void;
  /** True when the session may read catalog variants; false hides the box. */
  canResolve?: boolean;
  autoFocus?: boolean;
  className?: string;
}

export function ScanBox({
  id,
  label,
  onResolved,
  canResolve = true,
  autoFocus = false,
  className,
}: ScanBoxProps) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const [miss, setMiss] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  const lookup = useMutation({
    mutationFn: (barcode: string) => lookupVariantByBarcode(barcode),
    onSuccess: (variants, barcode) => {
      const hit = variants[0];
      if (hit === undefined) {
        setMiss(barcode);
        return;
      }
      setMiss(null);
      setValue("");
      onResolved(hit);
    },
    // A failed lookup is NOT an unknown barcode: saying "not found" would be a
    // lie that sends an operator hunting for a label that is perfectly valid.
    onError: () => {
      setMiss(null);
      setUnavailable(true);
    },
  });

  const resolve = (barcode: string) => {
    const trimmed = barcode.trim();
    if (trimmed === "" || !canResolve) return;
    setUnavailable(false);
    lookup.mutate(trimmed);
  };

  if (!canResolve) return null;

  return (
    <div className={className}>
      <ScannerCapture onScan={resolve} />
      <label htmlFor={id} className="mb-1.5 flex items-center gap-2 text-sm font-medium">
        <ScanBarcode className="size-4" aria-hidden="true" />
        {label}
      </label>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          // Barcodes are Latin codes — LTR even inside the RTL layout.
          dir="ltr"
          autoFocus={autoFocus}
          value={value}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key !== "Enter") return;
            event.preventDefault();
            resolve(value);
          }}
          placeholder={t("inventory.common.scanPlaceholder", "Scan or type a barcode…")}
        />
        {lookup.isPending ? <Spinner className="size-4" aria-hidden="true" /> : null}
      </div>
      {miss !== null ? (
        <p role="status" className="mt-1.5 text-xs text-muted-foreground">
          {t("inventory.common.barcodeMiss", "No SKU carries barcode {{barcode}}.", {
            barcode: miss,
          })}
        </p>
      ) : null}
      {unavailable ? (
        <p role="status" className="mt-1.5 text-xs text-muted-foreground">
          {t(
            "inventory.common.barcodeUnavailable",
            "Barcode lookup is unavailable right now — pick the SKU by hand.",
          )}
        </p>
      ) : null}
    </div>
  );
}
